import React from 'react';
import { Mail, Phone, MessageSquare } from 'lucide-react';

/**
 * Quick contact actions for a participant / organization.
 * All actions use the device's native apps:
 *   - Email → mailto: (opens default mail client with recipient pre-filled)
 *   - Call  → tel:    (opens phone dialer)
 *   - Text  → sms:    (opens default messaging app with number pre-filled)
 *
 * Props:
 *   email, phone   — strings (either may be absent)
 *   name           — optional, used only for hover titles
 *   subject, body  — optional, pre-fill for mailto and sms (body only)
 *   compact        — when true renders icon-only buttons
 */
const ContactButtons = ({ email, phone, name = '', subject = '', body = '', compact = false }) => {
  const hasEmail = !!email;
  const hasPhone = !!phone;

  const cls = compact
    ? 'p-2 rounded-lg border border-[#E9DDCF] hover:bg-[#FBF4EA] text-[#8B2E3C] disabled:opacity-40 disabled:cursor-not-allowed'
    : 'flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E9DDCF] hover:bg-[#FBF4EA] text-sm text-[#8B2E3C] font-medium disabled:opacity-40 disabled:cursor-not-allowed';

  const mailtoHref = (() => {
    if (!hasEmail) return undefined;
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (body) params.set('body', body);
    const qs = params.toString();
    return qs ? `mailto:${email}?${qs}` : `mailto:${email}`;
  })();

  const smsHref = (() => {
    if (!hasPhone) return undefined;
    // iOS uses '&' separator, Android uses '?' — '?body=' works on both modern platforms.
    return body ? `sms:${phone}?body=${encodeURIComponent(body)}` : `sms:${phone}`;
  })();

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <a
        href={mailtoHref}
        onClick={(e) => { e.stopPropagation(); if (!hasEmail) e.preventDefault(); }}
        className={cls}
        title={hasEmail ? `Email ${name || email}` : 'No email on file'}
        data-testid="contact-email-button"
        aria-disabled={!hasEmail}
      >
        <Mail className="w-4 h-4" />
        {!compact && <span>Email</span>}
      </a>
      <a
        href={hasPhone ? `tel:${phone}` : undefined}
        onClick={(e) => { e.stopPropagation(); if (!hasPhone) e.preventDefault(); }}
        className={cls}
        title={hasPhone ? `Call ${name || phone}` : 'No phone on file'}
        data-testid="contact-call-button"
        aria-disabled={!hasPhone}
      >
        <Phone className="w-4 h-4" />
        {!compact && <span>Call</span>}
      </a>
      <a
        href={smsHref}
        onClick={(e) => { e.stopPropagation(); if (!hasPhone) e.preventDefault(); }}
        className={cls}
        title={hasPhone ? `Text ${name || phone} (opens Messages app)` : 'No phone on file'}
        data-testid="contact-text-button"
        aria-disabled={!hasPhone}
      >
        <MessageSquare className="w-4 h-4" />
        {!compact && <span>Text</span>}
      </a>
    </div>
  );
};

export default ContactButtons;
